import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import useMediaSourceViews from '@/actions/useMediaSourceViews'
import { AudioSourceInfo, useUserContext } from '@/UserContext'
import * as Constants from '@/services/Constants'
import { Card } from '@/components/SessionSettings/internal/Card'
import { CardHeader } from '@/components/SessionSettings/internal/CardHeader'
import { BrowserAlert } from '@/components/SessionSettings/internal/BrowserAlert'
import { Tutorial } from '@/components/SessionSettings/internal/Tutorial'
import HeadphonesWaveIcon from '@/CustomIcons/HeadphonesWaveIcon'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { AudioQualitySelect } from '@/components/AudioQualitySelect'
import { ControlButton } from '@/UIComponents2/ControlButton'
import GearIcon from '@/CustomIcons/GearIcon'
import { Trans, useTranslation } from 'react-i18next'
import useMediaSourceActions from '@/functionalities/MediaSources/useMediaSourceActions'
import { Alert } from '@/UIComponents2/Alert'
import { Button } from '@/UIComponents2/Button'
import ElectronIPC from '@/services/ElectronIPC'
import { openAudioMIDISetup, sleep } from '@/utils'
import { useMemo, useState } from 'react'
import { Loader } from '@/components/Loader'
import { env } from '@/services/EnvironmentVariables'
import useComputerAudioSelectionController from '@/services/useComputerAudioSelectionController'
import computerAudioCoverJpg from '@/assets/tutorials/computer-audio-cover.jpg'
import AudioInputDeviceSettingsDialog from '@/pages/AudioInputDeviceSettings/AudioInputDeviceSettingsDialog'

export function ComputerAudioSection() {
    const [userState] = useUserContext()
    const { audioInputsClassifiedForUi } = useMediaSourceViews()
    const { updateSelectedAudioDeviceQualityPreset, toggleAudioDeviceSelection } = useMediaSelectionActions()
    const { toggleComputerAudioSelection, isComputerAudioChanging } = useComputerAudioSelectionController()
    const [openDeviceSettingsDialog, setOpenDeviceSettingsDialog] = useState<boolean>(false)
    const systemInputs = audioInputsClassifiedForUi['system']
    const selection = userState.mediaSelection.audioInputs.find((inp) => inp.source.isMuseAudioShare)
    const currQualityPresetCode = selection ? Constants.settingsToAudioQualityPesetCode(selection.settings) : 'enhanced'

    const handleChangeQuality = (code: Constants.AudioQualityPresetCode) => {
        if (selection) {
            updateSelectedAudioDeviceQualityPreset(selection, code)
        }
    }

    const handleSelectChange = async (inp: AudioSourceInfo) => {
        if (selection && selection.source.deviceId !== inp.deviceId) {
            toggleAudioDeviceSelection(selection.source)
            await sleep(0)
        }
        toggleComputerAudioSelection(inp)
    }

    return (
        <>
            <Card className="md:p-[10px] flex flex-col gap-[10px]" id="audio-computer">
                <div className="grid grid-cols[1fr] md:grid-cols-2 gap-[30px]">
                    <div className="flex flex-col justify-between pl-[20px] py-[15px] gap-4">
                        <CardHeader icon={HeadphonesWaveIcon} title="Sharing Computer Audio" />
                        {systemInputs.length > 0 && env.IS_ELECTRON && (
                            <div className="flex flex-col gap-1">
                                {systemInputs.map((inp) => {
                                    return (
                                        <div
                                            className="py-[10px] px-4 border-muse-toplit shadow-glow rounded-[14px] bg-white/3 cursor-pointer"
                                            key={inp.deviceId}
                                        >
                                            <Checkbox
                                                label={inp.displayLabel ?? 'Missing Virtual Device'}
                                                checked={selection?.deviceId === inp.deviceId}
                                                disabled={isComputerAudioChanging}
                                                onChange={() => {
                                                    handleSelectChange(inp)
                                                }}
                                            />
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                        <ErrorAlerts />

                        <div>
                            <div className="pb-2">Quality</div>
                            <div className="grid grid-cols-[1fr_min-content] gap-2">
                                <AudioQualitySelect
                                    disabled={selection === undefined}
                                    value={currQualityPresetCode}
                                    onChange={(code) => handleChangeQuality(code)}
                                />
                                <ControlButton
                                    variant="glow"
                                    disabled={selection === undefined}
                                    onClick={() => setOpenDeviceSettingsDialog(true)}
                                >
                                    <GearIcon />
                                </ControlButton>
                            </div>
                        </div>
                    </div>
                    <Tutorial
                        coverImageUrl={computerAudioCoverJpg}
                        source={
                            env.OS === 'win32'
                                ? 'https://museapp.sfo3.cdn.digitaloceanspaces.com/in-app-tutorials/computer-audio-win.mp4'
                                : 'https://museapp.sfo3.cdn.digitaloceanspaces.com/in-app-tutorials/computer-audio-mac.mp4'
                        }
                        sourceType="video/mp4"
                        title="Share Computer Audio"
                    />
                </div>
            </Card>
            {openDeviceSettingsDialog && selection && (
                <AudioInputDeviceSettingsDialog
                    mediaSource={selection.source}
                    onClose={() => setOpenDeviceSettingsDialog(false)}
                />
            )}
        </>
    )
}

function ErrorAlerts() {
    const [userState] = useUserContext()

    const { t } = useTranslation()

    const { audioInputsClassifiedForUi } = useMediaSourceViews()
    const { refreshMediaSourcesWithDebounce } = useMediaSourceActions()

    if (!env.IS_ELECTRON) {
        return <BrowserAlert action="share computer audio" />
    }

    return (
        <div>
            {/* conditionally show error message - no audio drivers detected */}
            {audioInputsClassifiedForUi.system.length === 0 && (
                <Alert variant="solid" level="warning">
                    <div>
                        {env.OS === 'win32'
                            ? t(
                                  'No audio devices detected. To share computer audio, you need to install a 3rd party audio driver such as VB Audio Cable.'
                              )
                            : t(
                                  'Muse Audio Share not detected. Please try restarting your computer or reinstalling Muse.'
                              )}
                    </div>
                    <div>
                        {env.OS === 'win32' && (
                            <Button
                                variant="link-dark"
                                onClick={() =>
                                    ElectronIPC.openLinkInBrowser(Constants.links.vb_audio_cable_download_link)
                                }
                            >
                                {t('Download VB Audio Cable')}
                            </Button>
                        )}
                    </div>
                </Alert>
            )}

            {/* error message - device muted */}
            {env.OS === 'darwin' && userState.mediaSourceStatus.audioInputs['museAudioShare']?.isMuted && (
                <Alert variant="solid" level="warning">
                    <div>
                        {t('Looks like Muse Audio Share is muted. You can unmute this device in Audio MIDI Setup.')}
                    </div>
                    <Button variant="link" onClick={() => openAudioMIDISetup()}>
                        {t('Open Audio MIDI Setup')}
                    </Button>
                    <Button variant="link" onClick={refreshMediaSourcesWithDebounce}>
                        {t('Check again')}
                    </Button>
                </Alert>
            )}

            {userState.mediaSelection.showSystemAudioOutputChangeConfirmation &&
                !userState.mediaSelection.isGlobalOutputDeviceChangeUpdating && <SystemAudioOutputChangeConfirmation />}
        </div>
    )
}

function SystemAudioOutputChangeConfirmation() {
    const [userState] = useUserContext()
    const { t } = useTranslation()
    const { setGlobalOutputDeviceToMuseAudioShare } = useMediaSelectionActions()

    const matchingLabel = useMemo(() => {
        const sharedSystemAudio = userState.mediaSelection.audioInputs.find((a) => a.source.isSystemAudio)
        if (sharedSystemAudio) {
            const match = sharedSystemAudio.source.matchingLoopbackSource
            if (match) {
                return match.label
            }
        }
        return ''
    }, [userState.mediaSelection])

    return (
        <Alert variant="solid" level="warning">
            <div>
                {env.OS === 'win32' ? (
                    <>
                        {matchingLabel ? (
                            <Trans
                                i18nKey={
                                    "You need to change your computer's audio output to <1>{{device}}</1> to share computer audio."
                                }
                                values={{ device: matchingLabel }}
                            >
                                You need to change your computer's audio output to <i>{matchingLabel}</i> to share
                                computer audio.
                            </Trans>
                        ) : (
                            <>{t("You need to change your computer's audio output to share computer audio.")}</>
                        )}
                    </>
                ) : (
                    <>
                        {t(
                            "You need to change your computer's audio output to Muse Audio Share to share computer audio."
                        )}
                    </>
                )}
            </div>
            <div className="flex justify-between mt-1">
                <div className="flex gap-2 items-center">
                    <Button
                        variant="link-dark"
                        onClick={() => {
                            setGlobalOutputDeviceToMuseAudioShare({ undoWhenScreenShareEnds: false })
                        }}
                        disabled={userState.mediaSelection.isGlobalOutputDeviceChangeUpdating}
                    >
                        {t('Change it for me')}
                    </Button>
                    {userState.mediaSelection.isGlobalOutputDeviceChangeUpdating && <Loader width="33px" />}
                </div>
            </div>
        </Alert>
    )
}
